import React, { useCallback, useEffect, useState } from 'react';
import { useInspectorSections } from './use-inspector-sections';
import { CollapsibleContent } from './collapsible-content';
import {
  FEATURE_ALIGNMENT_CONTROL,
  FEATURE_BORDER_RADIUS_CONTROL,
  FEATURE_COLOR_CONTROL,
  FEATURE_DIMENSIONS_CONTROL,
  FEATURE_OPACITY_CONTROL,
  FEATURE_POSITION_CONTROL,
  FEATURE_ROTATION_CONTROL,
  FEATURE_TEXT_STROKE_COLOR_CONTROL,
  FEATURE_TEXT_STROKE_WIDTH_CONTROL,
} from '../flags';
import { SolidItem } from '../items/solid/solid-item-type';
import { defaultTextShadow } from '../items/text/text-item-type';
import { ColorInspector } from './color-inspector';
import {
  AccordionHeader,
  cn,
  ControlsHeader,
  ToolButton,
} from '@crabs-ai/ds-react';
import {
  Delete,
  Remove,
  Visibility,
  VisibilityOffFill,
} from '@crabs-ai/web-icons';
import { AlignmentControls } from './controls/alignment-controls';
import { BorderRadiusControl } from './controls/border-radius-controls';
import { DimensionsControls } from './controls/dimensions-controls';
import { OpacityControls } from './controls/opacity-controls';
import { PositionControl } from './controls/position-control';
import { RotationControl } from './controls/rotation-controls';
import { StrokeWidthControls } from './controls/stroke-width-controls';
import { AnimationControls } from './controls/animation-controls';
import { EMPTY_ANIMATIONS } from '../items/animation/animation-item-type';
import ShadowControls from '@/features/remotion-editor/editor/inspector/controls/shadow-controls';
import { useTranslations } from 'next-intl';
import { changeItemsWithSelection } from '../state/actions/change-items';
import { useWriteContext } from '../utils/use-context';
import { useTargetItemIds } from './utils/use-target-item-ids';
import { TimingControls } from './controls/timing-controls';

const SOLID_DEFAULTS = {
  time: true,
  positionShape: true,
  color: true,
  stroke: true,
  shadow: true,
};

const SolidInspectorUnmemoized: React.FC<{
  item: SolidItem;
  isMulti?: boolean;
  collapseAllTrigger?: number;
  expandAllTrigger?: number;
}> = ({ item, collapseAllTrigger = 0, expandAllTrigger = 0 }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(item.id);

  // ── Active 상태 (로컬 state) ──
  // 모듈 레벨 캐시 + lazy initializer로 언마운트/리마운트 시에도 eye-off 상태 유지.
  const deriveSectionActive = useCallback(
    (_id: string) => {
      const derived = {
        color: item.color !== 'transparent',
        stroke: (item.strokeWidth ?? 0) > 0,
        shadow: item.shadow?.visible === true,
      };
      return derived;
    },
    [item.color, item.strokeWidth, item.shadow?.visible],
  );

  const [sectionActive, setSectionActive] = useState(() =>
    deriveSectionActive(item.id),
  );

  useEffect(() => {
    setSectionActive(deriveSectionActive(item.id));
  }, [item.id, deriveSectionActive]);

  const { openSections, setOpenSections, toggleSection } = useInspectorSections(
    {
      itemId: item.id,
      defaults: SOLID_DEFAULTS,
      collapseAllTrigger,
      expandAllTrigger,
      sectionActive,
    },
  );
  const isColorEyeOff = item.colorVisible === false;
  const isStrokeEyeOff = item.strokeVisible === false;
  const isShadowEyeOff = item.shadowVisible === false;

  // ── Color 핸들러 ──
  const handleActivateColor = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => ({
          ...i,
          color: '#4A90D9',
          colorVisible: true,
        })),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, color: true }));
    setOpenSections((prev) => ({ ...prev, color: true }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleDeactivateColor = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => ({
          ...i,
          color: 'transparent',
          colorVisible: true,
        })),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, color: false }));
    setOpenSections((prev) => ({ ...prev, color: false }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleToggleColorVisibility = useCallback(() => {
    const nextVisible = isColorEyeOff;
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => ({
          ...i,
          colorVisible: nextVisible,
        })),
      commitToUndoStack: true,
    });
  }, [isColorEyeOff, setState, item.id, targetIds]);

  // ── Stroke 핸들러 ──
  const handleActivateStroke = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'solid') return i;
          return {
            ...i,
            strokeWidth: 10,
            strokeColor: '#000000',
            strokeVisible: true,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, stroke: true }));
    setOpenSections((prev) => ({ ...prev, stroke: true }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleDeactivateStroke = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'solid') return i;
          return {
            ...i,
            strokeWidth: 0,
            strokeColor: 'transparent',
            strokeVisible: true,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, stroke: false }));
    setOpenSections((prev) => ({ ...prev, stroke: false }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleToggleStrokeVisibility = useCallback(() => {
    const nextVisible = isStrokeEyeOff;
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'solid') return i;
          return { ...i, strokeVisible: nextVisible };
        }),
      commitToUndoStack: true,
    });
  }, [isStrokeEyeOff, setState, item.id, targetIds]);

  // ── Shadow 핸들러 ──
  const handleActivateShadow = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'solid') return i;
          return {
            ...i,
            shadow: { ...defaultTextShadow, visible: true },
            shadowVisible: true,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, shadow: true }));
    setOpenSections((prev) => ({ ...prev, shadow: true }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleDeactivateShadow = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'solid') return i;
          return {
            ...i,
            shadow: { ...defaultTextShadow },
            shadowVisible: undefined,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, shadow: false }));
    setOpenSections((prev) => ({ ...prev, shadow: false }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleToggleShadowVisibility = useCallback(() => {
    const nextVisible = isShadowEyeOff;
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'solid') return i;
          return { ...i, shadowVisible: nextVisible };
        }),
      commitToUndoStack: true,
    });
  }, [isShadowEyeOff, setState, item.id, targetIds]);

  const strokeColor = item.strokeColor ?? '#000000';

  return (
    <div className="layout-padding-bottom">
      <div className="flex flex-col gap-2xs">
        {/* Animation 섹션 */}
        <div className="border-b border-sys-style-neutral-light">
          <AnimationControls
            itemId={item.id}
            animations={item.animations ?? EMPTY_ANIMATIONS}
            durationInFrames={item.durationInFrames}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.timing.title')}
            isOpened={openSections.time}
            onClick={() => toggleSection('time')}
          />
          <CollapsibleContent isOpen={openSections.time}>
            <div className="px-md py-4xs">
              <TimingControls itemId={item.id} />
            </div>
          </CollapsibleContent>
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.text.position-shape')}
            isOpened={openSections.positionShape}
            onClick={() => toggleSection('positionShape')}
          />
          <CollapsibleContent isOpen={openSections.positionShape}>
            <>
              <div className="px-md py-4xs">
                {FEATURE_ALIGNMENT_CONTROL && (
                  <AlignmentControls itemId={item.id} />
                )}
                {FEATURE_POSITION_CONTROL && (
                  <PositionControl itemId={item.id} />
                )}
                {FEATURE_DIMENSIONS_CONTROL && (
                  <DimensionsControls itemId={item.id} />
                )}
                {FEATURE_ROTATION_CONTROL && (
                  <RotationControl
                    rotation={item.rotation}
                    itemId={item.id}
                  />
                )}
              </div>
              <div className="flex w-full items-center gap-4xs px-md pb-2xs">
                {FEATURE_OPACITY_CONTROL && (
                  <div className="flex-1">
                    <OpacityControls
                      opacity={item.opacity}
                      itemId={item.id}
                    />
                  </div>
                )}
                {FEATURE_BORDER_RADIUS_CONTROL && (
                  <div className="flex-1">
                    <BorderRadiusControl
                      borderRadius={item.borderRadius}
                      itemId={item.id}
                    />
                  </div>
                )}
              </div>
            </>
          </CollapsibleContent>
        </div>
        {/* 색상 섹션 */}
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <ControlsHeader
            size="sm"
            title={t('Editor.inspector.text.color')}
            isActive={sectionActive.color}
            isOpened={openSections.color}
            onAction={handleActivateColor}
            onClick={() => toggleSection('color')}
            actionTooltip="속성 추가"
            activeTrailingNode={
              <ToolButton
                size="sm"
                onClick={handleDeactivateColor}
                isShowTooltip
                tooltipNode="속성 삭제"
              >
                <Delete />
              </ToolButton>
            }
          />
          <CollapsibleContent
            isOpen={sectionActive.color && openSections.color}
          >
            <div className="flex flex-col gap-4xs px-md py-4xs">
              <div className="flex items-end-safe gap-4xs">
                <div
                  className={cn(
                    'flex flex-1 items-end-safe gap-4xs',
                    isColorEyeOff && 'pointer-events-none opacity-40',
                  )}
                >
                  {FEATURE_COLOR_CONTROL && (
                    <ColorInspector
                      color={item.color}
                      itemId={item.id}
                      colorType="color"
                      accessibilityLabel="Fill color"
                      itemOpacity={item.opacity}
                    />
                  )}
                </div>
                <ToolButton
                  aria-label="Toggle color visibility"
                  onClick={handleToggleColorVisibility}
                  isShowTooltip
                  tooltipNode={!isColorEyeOff ? '속성 끄기' : '속성 켜기'}
                  state={isColorEyeOff ? 'selected' : 'default'}
                  isTransparent={false}
                  className="h-[36px] w-[36px]"
                >
                  {!isColorEyeOff ? (
                    <Visibility className="size-4" />
                  ) : (
                    <VisibilityOffFill className="size-4" />
                  )}
                </ToolButton>
                <ToolButton
                  aria-label="Remove color"
                  onClick={handleDeactivateColor}
                  isShowTooltip
                  tooltipNode="속성 삭제"
                  className="h-[36px] w-[36px] text-sys-cont-neutral-default"
                >
                  <Remove className="size-4" />
                </ToolButton>
              </div>
            </div>
          </CollapsibleContent>
        </div>
        {/* 테두리 섹션 */}
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <ControlsHeader
            size="sm"
            title={t('Editor.inspector.text.stroke')}
            isActive={sectionActive.stroke}
            isOpened={openSections.stroke}
            onAction={handleActivateStroke}
            onClick={() => toggleSection('stroke')}
            actionTooltip="속성 추가"
            activeTrailingNode={
              <ToolButton
                size="sm"
                onClick={handleDeactivateStroke}
                isShowTooltip
                tooltipNode="속성 삭제"
              >
                <Delete />
              </ToolButton>
            }
          />
          <CollapsibleContent
            isOpen={sectionActive.stroke && openSections.stroke}
          >
            <div className="flex flex-col gap-4xs px-md py-4xs">
              <div className="flex items-end-safe gap-4xs">
                <div
                  className={cn(
                    'flex flex-1 items-end-safe gap-4xs',
                    isStrokeEyeOff && 'pointer-events-none opacity-40',
                  )}
                >
                  {FEATURE_TEXT_STROKE_COLOR_CONTROL && (
                    <ColorInspector
                      color={strokeColor}
                      itemId={item.id}
                      colorType="strokeColor"
                      accessibilityLabel="Stroke color"
                      itemOpacity={item.opacity}
                    />
                  )}
                </div>
                <ToolButton
                  aria-label="Toggle stroke visibility"
                  onClick={handleToggleStrokeVisibility}
                  isShowTooltip
                  tooltipNode={!isStrokeEyeOff ? '속성 끄기' : '속성 켜기'}
                  state={isStrokeEyeOff ? 'selected' : 'default'}
                  isTransparent={false}
                  className="h-[36px] w-[36px]"
                >
                  {!isStrokeEyeOff ? (
                    <Visibility className="size-4" />
                  ) : (
                    <VisibilityOffFill className="size-4" />
                  )}
                </ToolButton>
                <ToolButton
                  aria-label="Remove stroke"
                  onClick={handleDeactivateStroke}
                  isShowTooltip
                  tooltipNode="속성 삭제"
                  className="h-[36px] w-[36px] text-sys-cont-neutral-default"
                >
                  <Remove className="size-4" />
                </ToolButton>
              </div>
              <div
                className={cn(
                  isStrokeEyeOff && 'pointer-events-none opacity-40',
                )}
              >
                {FEATURE_TEXT_STROKE_WIDTH_CONTROL && (
                  <StrokeWidthControls
                    strokeWidth={item.strokeWidth ?? 0}
                    itemId={item.id}
                  />
                )}
              </div>
            </div>
          </CollapsibleContent>
        </div>
        {/* 그림자 효과 섹션 */}
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <ControlsHeader
            size="sm"
            title={t('Editor.inspector.shadow-style')}
            isActive={sectionActive.shadow}
            isOpened={openSections.shadow}
            onAction={handleActivateShadow}
            onClick={() => toggleSection('shadow')}
            actionTooltip="속성 추가"
            activeTrailingNode={
              <ToolButton
                size="sm"
                onClick={handleDeactivateShadow}
                isShowTooltip
                tooltipNode="속성 삭제"
              >
                <Delete />
              </ToolButton>
            }
          />
          <CollapsibleContent
            isOpen={sectionActive.shadow && openSections.shadow}
          >
            <div className={'px-md py-4xs'}>
              <ShadowControls
                itemId={item.id}
                shadow={item.shadow}
                isEyeOff={isShadowEyeOff}
                onToggleEyeVisibility={handleToggleShadowVisibility}
                onDeactivate={() => {
                  setSectionActive((prev) => ({ ...prev, shadow: false }));
                  setOpenSections((prev) => ({ ...prev, shadow: false }));
                }}
              />
            </div>
          </CollapsibleContent>
        </div>
      </div>
    </div>
  );
};

export const SolidInspector = React.memo(SolidInspectorUnmemoized);
